// When a photo was taken, written into the JPEG the uploader sends, for the
// one case the browser leaves it out: iOS Safari's camera. WebKit re-encodes a
// photo taken from a file input as a JPEG named "image.jpg", without any of
// its EXIF data (https://bugs.webkit.org/show_bug.cgi?id=263192), so the photo
// stamps (PhotoTimestamp) would read "Aufnahmezeit unbekannt". Such a file is
// made the moment the photo is taken, so its own time is when.
//
// As the app does for its camera (imeos/cgx-mobile#157, whose EXIF writer
// this is): just DateTimeOriginal and OffsetTimeOriginal, nothing else. A
// file that names a time of its own keeps it, so an old photo does not turn
// new by being renamed.

const CAMERA_FILE_NAME = 'image.jpg'
// Enough for the file to be passed from the camera to the page, little enough
// that an old file renamed image.jpg does not pass for a fresh photo
const FRESH_MS = 10 * 60 * 1000

export function isCameraCapture(file, now = Date.now()) {
  return file.name === CAMERA_FILE_NAME && now - file.lastModified < FRESH_MS
}

// The blob to upload, with the capture time written into it if the original
// file was taken with iOS Safari's camera. Anything amiss, it goes up as it is.
export async function withCameraCaptureTime(original, blob) {
  if (!isCameraCapture(original) || blob.type !== 'image/jpeg') return blob

  try {
    const jpeg = new Uint8Array(await blob.arrayBuffer())
    if (hasExifDate(jpeg)) return blob

    const takenAt = new Date(original.lastModified)
    const segment = captureTimeExifSegment({ dateTime: exifDateTime(takenAt), offset: exifOffset(takenAt) })
    const stamped = new Blob([withExifSegment(jpeg, segment)], { type: 'image/jpeg' })
    stamped.name = blob.name || original.name
    stamped.lastModifiedDate = new Date()
    return stamped
  } catch {
    return blob
  }
}

const pad = value => String(value).padStart(2, '0')

// "2026:11:04 14:32:10" on the phone's clock, as its camera writes it
function exifDateTime(date) {
  const day = [date.getFullYear(), date.getMonth() + 1, date.getDate()]
  const time = [date.getHours(), date.getMinutes(), date.getSeconds()]
  return `${day.map(pad).join(':')} ${time.map(pad).join(':')}`
}

// "+01:00", how far the phone's clock is ahead of UTC at that moment
function exifOffset(date) {
  const minutes = -date.getTimezoneOffset()
  const sign = minutes < 0 ? '-' : '+'
  const absolute = Math.abs(minutes)
  return `${sign}${pad(Math.floor(absolute / 60))}:${pad(absolute % 60)}`
}

const TAG_EXIF_IFD = 0x8769
const TAG_EXIF_VERSION = 0x9000
const TAG_DATE_TIME_ORIGINAL = 0x9003
const TAG_OFFSET_TIME_ORIGINAL = 0x9011
const TYPE_ASCII = 2
const TYPE_LONG = 4
const TYPE_UNDEFINED = 7

const EXIF_HEADER = [0x45, 0x78, 0x69, 0x66, 0x00, 0x00] // "Exif\0\0"

const asciiBytes = text => Uint8Array.from(text, char => char.charCodeAt(0))
const nulTerminated = text => asciiBytes(`${text}\0`)

// A JPEG APP1 segment with nothing but the capture time: IFD0 only points to
// an Exif IFD holding ExifVersion, DateTimeOriginal and OffsetTimeOriginal.
// Tags in ascending order, big-endian ("MM").
function captureTimeExifSegment({ dateTime, offset }) {
  const entries = [
    // OffsetTimeOriginal came with Exif 2.31
    { tag: TAG_EXIF_VERSION, type: TYPE_UNDEFINED, value: asciiBytes('0232') },
    { tag: TAG_DATE_TIME_ORIGINAL, type: TYPE_ASCII, value: nulTerminated(dateTime) },
    { tag: TAG_OFFSET_TIME_ORIGINAL, type: TYPE_ASCII, value: nulTerminated(offset) }
  ]

  const tiff = tiffWithExifIfd(entries)
  const segment = new Uint8Array(4 + EXIF_HEADER.length + tiff.length)
  const view = new DataView(segment.buffer)
  view.setUint16(0, 0xffe1)
  // The length counts itself, not the marker
  view.setUint16(2, segment.length - 2)
  segment.set(EXIF_HEADER, 4)
  segment.set(tiff, 4 + EXIF_HEADER.length)
  return segment
}

// TIFF data whose IFD0 holds just the pointer to the Exif IFD. Offsets count
// from the TIFF header. A value of up to 4 bytes sits in its entry, a longer
// one after the IFD, starting on an even offset.
function tiffWithExifIfd(entries) {
  const ifd0Offset = 8
  const exifIfdOffset = ifd0Offset + ifdSize(1)
  let valueOffset = exifIfdOffset + ifdSize(entries.length)
  const valuesSize = entries
    .filter(({ value }) => value.length > 4)
    .reduce((size, { value }) => size + value.length + (value.length % 2), 0)

  const tiff = new Uint8Array(valueOffset + valuesSize)
  const view = new DataView(tiff.buffer)
  view.setUint16(0, 0x4d4d) // "MM"
  view.setUint16(2, 42)
  view.setUint32(4, ifd0Offset)

  view.setUint16(ifd0Offset, 1)
  view.setUint16(ifd0Offset + 2, TAG_EXIF_IFD)
  view.setUint16(ifd0Offset + 4, TYPE_LONG)
  view.setUint32(ifd0Offset + 6, 1)
  view.setUint32(ifd0Offset + 10, exifIfdOffset)
  view.setUint32(ifd0Offset + 14, 0) // no IFD1, so no thumbnail

  view.setUint16(exifIfdOffset, entries.length)
  entries.forEach(({ tag, type, value }, index) => {
    const entryOffset = exifIfdOffset + 2 + index * 12
    view.setUint16(entryOffset, tag)
    view.setUint16(entryOffset + 2, type)
    view.setUint32(entryOffset + 4, value.length)
    if (value.length <= 4) {
      tiff.set(value, entryOffset + 8)
    } else {
      view.setUint32(entryOffset + 8, valueOffset)
      tiff.set(value, valueOffset)
      valueOffset += value.length + (value.length % 2)
    }
  })
  view.setUint32(exifIfdOffset + 2 + entries.length * 12, 0)
  return tiff
}

// Entry count, 12 bytes per entry, offset of the next IFD
const ifdSize = entryCount => 2 + entryCount * 12 + 4

const SOI = 0xd8
const SOS = 0xda
const APP1 = 0xe1

// The segments before the image data, each { start, end }
function headerSegments(jpeg) {
  if (jpeg[0] !== 0xff || jpeg[1] !== SOI) throw new Error('Not a JPEG')

  const segments = []
  let offset = 2
  while (offset + 4 <= jpeg.length && jpeg[offset] === 0xff && jpeg[offset + 1] !== SOS) {
    const end = offset + 2 + ((jpeg[offset + 2] << 8) | jpeg[offset + 3])
    segments.push({ start: offset, end })
    offset = end
  }
  return { segments, imageData: offset }
}

const isExifSegment = (jpeg, { start }) =>
  jpeg[start + 1] === APP1 && EXIF_HEADER.every((byte, index) => jpeg[start + 4 + index] === byte)

const latin1 = new TextDecoder('latin1')

// Whether its EXIF data names any time, as "2026:11:04 14:32:10"
function hasExifDate(jpeg) {
  return headerSegments(jpeg).segments
    .filter(segment => isExifSegment(jpeg, segment))
    .some(({ start, end }) => /\d{4}:\d\d:\d\d \d\d:\d\d:\d\d/.test(latin1.decode(jpeg.subarray(start, end))))
}

// The JPEG with that APP1 segment right after its start marker, in place of
// any EXIF it had. Every other segment, such as a color profile, stays.
function withExifSegment(jpeg, segment) {
  const { segments, imageData } = headerSegments(jpeg)
  const parts = [
    jpeg.subarray(0, 2),
    segment,
    ...segments.filter(header => !isExifSegment(jpeg, header)).map(({ start, end }) => jpeg.subarray(start, end)),
    jpeg.subarray(imageData)
  ]

  const result = new Uint8Array(parts.reduce((size, part) => size + part.length, 0))
  parts.reduce((position, part) => {
    result.set(part, position)
    return position + part.length
  }, 0)
  return result
}
